// Reporte "Control diario de producción": réplica de la hoja mensual (ENE…DIC) del Excel
// "Control_Diario_Produccion" que llenaba el cliente a mano.
//
// - buildDailyInputs: arma las celdas amarillas (columnas C…K) desde los datos del sistema.
// - computeControlDiario: calcula las columnas L…AG, la fila TOTAL MES, PROMEDIO DÍA y la banda
//   de KPIs con EXACTAMENTE las mismas fórmulas del Excel (incluida la regla de cuándo una celda
//   queda vacía).
// - excelFormulas*: las mismas fórmulas en texto, para la descarga en Excel.
//
// Convención: una celda vacía del Excel ("") se representa con null.

export const MESES = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];
export const MESES_CORTOS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'];
const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

// Fila 9 del Excel = día 1; siempre 31 filas (9…39), como la plantilla.
export const FIRST_ROW = 9;
export const ROWS = 31;
export const TOTAL_ROW = 40;
export const AVG_ROW = 41;

export const DEFAULT_PARAMS = { factorCarga: 90, toleranciaVariacion: 5, toleranciaClienteAlistado: 5 };

// Parámetros de la lavandería (en %) -> fracciones que usan las fórmulas
export const resolveParams = (laundry) => {
  const cp = { ...DEFAULT_PARAMS, ...(laundry?.indicadores?.controlProduccion || {}) };
  return {
    fc: Number(cp.factorCarga) / 100,
    tolVar: Number(cp.toleranciaVariacion) / 100,
    tolCliente: Number(cp.toleranciaClienteAlistado) / 100,
    raw: cp,
  };
};

// Día local de Colombia (UTC-5 fijo) de una fecha
const bogotaDay = (value) => {
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return null;
  return new Date(t - 5 * 3600 * 1000).toISOString().slice(0, 10);
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate(); // month 1-12

// ─── Entradas (celdas amarillas C…K) ────────────────────────────────────────
export function buildDailyInputs({ year, month, cycles = [], receptions = [], alistamientos = [], hours = [], supervisor = '' }) {
  const prefix = `${year}-${String(month).padStart(2, '0')}-`;
  const byDay = new Map();
  const get = (day) => {
    if (!byDay.has(day)) byDay.set(day, { abortados: [] });
    return byDay.get(day);
  };
  const add = (o, k, v) => { o[k] = (o[k] || 0) + (Number(v) || 0); };
  const inMonth = (key) => (key && key.startsWith(prefix) ? Number(key.slice(8)) : null);

  cycles.forEach((c) => {
    const d = inMonth(bogotaDay(c.fecha));
    if (!d) return;
    const o = get(d);
    if (c.abortado === true) {
      o.abortados.push((c.razonAbortado || 'Sin razón').trim());
    } else {
      add(o, 'ciclos', c.ciclos);
      add(o, 'kgCiclos', c.kilosLavados);
      o.hasCiclos = true;
    }
  });
  receptions.forEach((r) => {
    const d = inMonth(bogotaDay(r.createdAt));
    if (!d) return;
    const o = get(d);
    add(o, 'lotes', 1);
    add(o, 'kgLotes', r.pesoKg);
  });
  alistamientos.forEach((a) => {
    if (a.estado === 'Eliminado') return;
    const d = inMonth(bogotaDay(a.fechaCreacion));
    if (!d) return;
    const o = get(d);
    add(o, 'kgAlist', (Number(a.pesoTotal) || 0) / 1000);
    add(o, 'prendas', a.totalItems);
    o.hasAlist = true;
  });
  hours.forEach((h) => {
    const d = inMonth(bogotaDay(h.fecha));
    if (!d) return;
    const o = get(d);
    add(o, 'horas', h.totalHorasAutorizadas);
    o.hasHoras = true;
  });

  const n = daysInMonth(year, month);
  const rows = [];
  for (let d = 1; d <= ROWS; d += 1) {
    const valid = d <= n;
    const o = valid ? byDay.get(d) : null;
    const date = valid ? new Date(year, month - 1, d) : null;
    const tieneDatos = !!o && (o.hasCiclos || o.lotes || o.hasAlist || o.hasHoras);
    const round3 = (v) => (v == null ? null : Math.round(v * 1000) / 1000);
    rows.push({
      day: d,
      date,
      dia: date ? DIAS[(date.getDay() + 6) % 7] : null,
      C: o?.hasCiclos ? o.ciclos : null,
      D: o?.hasCiclos ? round3(o.kgCiclos) : null,
      E: o?.lotes ? o.lotes : null,
      F: o?.lotes ? round3(o.kgLotes) : null,
      G: o?.hasAlist ? round3(o.kgAlist) : null,
      H: o?.hasAlist ? o.prendas : null,
      I: o?.hasHoras ? round3(o.horas) : null,
      J: tieneDatos && supervisor ? supervisor : null,
      K: o?.abortados?.length ? `${o.abortados.length} ciclo(s) abortado(s): ${o.abortados.join(', ')}` : null,
    });
  }
  return rows;
}

// ─── Cálculos (columnas L…AG + totales) ─────────────────────────────────────
const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
const N = (v) => (isNum(v) ? v : 0);
const blank = (v) => v === null || v === undefined || v === '';
const sumIfs = (sumCol, critCol, rows) => rows.reduce((s, r) => (N(r[critCol]) > 0 ? s + N(r[sumCol]) : s), 0);
const sum = (col, rows) => rows.reduce((s, r) => s + N(r[col]), 0);
const count = (col, rows) => rows.filter((r) => isNum(r[col])).length;
const INPUT_COLS = ['C', 'D', 'E', 'F', 'G', 'H', 'I'];

export function computeControlDiario(inputRows, params) {
  const { fc, tolCliente } = params;
  const rows = inputRows.map((r) => ({ ...r }));

  rows.forEach((r, i) => {
    const upTo = rows.slice(0, i + 1);
    const countCI = INPUT_COLS.filter((c) => isNum(r[c])).length;

    r.L = blank(r.C) || N(r.C) === 0 ? null : r.D / r.C;
    r.M = blank(r.F) || N(r.F) === 0 || blank(r.D) ? null : (r.D * fc - r.F) / r.F;
    r.N = N(r.I) === 0 || blank(r.C) ? null : r.C / r.I;
    r.O = N(r.I) === 0 || blank(r.D) ? null : (r.D * fc) / r.I;
    r.P = N(r.I) === 0 || blank(r.F) ? null : r.F / r.I;
    r.Q = N(r.I) === 0 || blank(r.G) ? null : r.G / r.I;
    r.R = N(r.I) === 0 || blank(r.H) ? null : r.H / r.I;

    if (countCI === 0) {
      ['S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z', 'AA', 'AB'].forEach((c) => { r[c] = null; });
    } else {
      r.S = sum('D', upTo);
      r.T = sum('F', upTo);
      r.U = sum('G', upTo);
      r.V = sum('H', upTo);
      r.W = sum('I', upTo);
      const fConD = sumIfs('F', 'D', upTo);
      r.X = N(fConD) === 0 ? null : (sumIfs('D', 'F', upTo) * fc - fConD) / fConD;
      const factConG = sumIfs('D', 'G', upTo) * fc;
      r.Y = N(r.U) === 0 ? null : (factConG === 0 ? null : (r.U - factConG) / factConG);
      r.Z = N(r.W) === 0 ? null : (sumIfs('D', 'I', upTo) * fc) / r.W;
      r.AA = N(r.W) === 0 ? null : sumIfs('G', 'I', upTo) / r.W;
      r.AB = N(r.W) === 0 ? null : sumIfs('H', 'I', upTo) / r.W;
    }
    r.AC = blank(r.O) || N(r.Z) === 0 ? null : (r.O - r.Z) / r.Z;
    r.AD = blank(r.D) ? null : r.D * fc;
    r.AE = N(r.D) === 0 || blank(r.G) ? null : N(r.AD) - N(r.G);
    r.AF = blank(r.AE) ? null : upTo.reduce((s, x) => s + N(x.AE), 0);
    if (blank(r.AE)) r.AG = null;
    else if (N(r.AD) < N(r.G)) r.AG = 'BAJO ALISTADO';
    else if (N(r.AD) > N(r.G) * (1 + tolCliente)) r.AG = 'SOBRE TOLERANCIA';
    else r.AG = 'OK';
  });

  // Fila 40: TOTAL MES
  const t = {};
  INPUT_COLS.forEach((c) => { t[c] = sum(c, rows); });
  t.L = t.C === 0 ? 0 : t.D / t.C;
  const fConD = sumIfs('F', 'D', rows);
  t.M = fConD === 0 ? 0 : (sumIfs('D', 'F', rows) * fc - fConD) / fConD;
  t.N = t.I === 0 ? 0 : sumIfs('C', 'I', rows) / t.I;
  t.O = t.I === 0 ? 0 : (sumIfs('D', 'I', rows) * fc) / t.I;
  t.P = t.I === 0 ? 0 : sumIfs('F', 'I', rows) / t.I;
  t.Q = t.I === 0 ? 0 : sumIfs('G', 'I', rows) / t.I;
  t.R = t.I === 0 ? 0 : sumIfs('H', 'I', rows) / t.I;
  t.X = t.M;
  const factConG = sumIfs('D', 'G', rows) * fc;
  t.Y = t.G === 0 ? 0 : (factConG === 0 ? 0 : (t.G - factConG) / factConG);
  t.Z = t.O;
  t.AA = t.Q;
  t.AB = t.R;
  t.AD = sum('AD', rows);
  t.AE = count('AE', rows) === 0 ? null : sum('AE', rows);
  t.AG = `${rows.filter((r) => r.AG === 'BAJO ALISTADO').length} bajo · ${rows.filter((r) => r.AG === 'SOBRE TOLERANCIA').length} sobre`;

  // Fila 41: PROMEDIO DÍA
  const avg = {};
  INPUT_COLS.forEach((c) => {
    const k = count(c, rows);
    avg[c] = k === 0 ? 0 : sum(c, rows) / k;
  });

  // Banda de KPIs (fila 5)
  const kpis = {
    diasRegistrados: count('D', rows),
    kgCiclos: t.D,
    kgLotes: t.F,
    kgAlist: t.G,
    prendas: t.H,
    varFactLotes: t.X,
    varAlistFact: t.Y,
    horas: t.I,
    kgFactHora: t.O,
    kgAlistHora: t.Q,
    prendasHora: t.R,
  };

  return { rows, totals: t, averages: avg, kpis };
}

// ─── Fórmulas Excel (idénticas a la plantilla del cliente) ──────────────────
// Devuelve { col: 'fórmula sin =' } para la fila de datos `r` (9…39), día `d`, mes `m` (1-12).
export function excelFormulasForRow(r, d, m) {
  const F9 = FIRST_ROW;
  return {
    A: `IF(${d}<=DAY(EOMONTH(DATE(CONFIG!$B$6,${m},1),0)),DATE(CONFIG!$B$6,${m},${d}),"")`,
    B: `IF(A${r}="","",CHOOSE(WEEKDAY(A${r},2),"Lun","Mar","Mié","Jue","Vie","Sáb","Dom"))`,
    L: `IF(OR(C${r}="",N(C${r})=0),"",D${r}/C${r})`,
    M: `IF(OR(F${r}="",N(F${r})=0,D${r}=""),"",(D${r}*CONFIG!$B$17-F${r})/F${r})`,
    N: `IF(OR(N(I${r})=0,C${r}=""),"",C${r}/I${r})`,
    O: `IF(OR(N(I${r})=0,D${r}=""),"",D${r}*CONFIG!$B$17/I${r})`,
    P: `IF(OR(N(I${r})=0,F${r}=""),"",F${r}/I${r})`,
    Q: `IF(OR(N(I${r})=0,G${r}=""),"",G${r}/I${r})`,
    R: `IF(OR(N(I${r})=0,H${r}=""),"",H${r}/I${r})`,
    S: `IF(COUNT(C${r}:I${r})=0,"",SUM(D$${F9}:D${r}))`,
    T: `IF(COUNT(C${r}:I${r})=0,"",SUM(F$${F9}:F${r}))`,
    U: `IF(COUNT(C${r}:I${r})=0,"",SUM(G$${F9}:G${r}))`,
    V: `IF(COUNT(C${r}:I${r})=0,"",SUM(H$${F9}:H${r}))`,
    W: `IF(COUNT(C${r}:I${r})=0,"",SUM(I$${F9}:I${r}))`,
    X: `IF(OR(COUNT(C${r}:I${r})=0,N(SUMIFS(F$${F9}:F${r},D$${F9}:D${r},">0"))=0),"",(SUMIFS(D$${F9}:D${r},F$${F9}:F${r},">0")*CONFIG!$B$17-SUMIFS(F$${F9}:F${r},D$${F9}:D${r},">0"))/SUMIFS(F$${F9}:F${r},D$${F9}:D${r},">0"))`,
    Y: `IF(OR(COUNT(C${r}:I${r})=0,N(U${r})=0),"",IFERROR((U${r}-SUMIFS(D$${F9}:D${r},G$${F9}:G${r},">0")*CONFIG!$B$17)/(SUMIFS(D$${F9}:D${r},G$${F9}:G${r},">0")*CONFIG!$B$17),""))`,
    Z: `IF(OR(COUNT(C${r}:I${r})=0,N(W${r})=0),"",SUMIFS(D$${F9}:D${r},I$${F9}:I${r},">0")*CONFIG!$B$17/W${r})`,
    AA: `IF(OR(COUNT(C${r}:I${r})=0,N(W${r})=0),"",SUMIFS(G$${F9}:G${r},I$${F9}:I${r},">0")/W${r})`,
    AB: `IF(OR(COUNT(C${r}:I${r})=0,N(W${r})=0),"",SUMIFS(H$${F9}:H${r},I$${F9}:I${r},">0")/W${r})`,
    AC: `IF(OR(O${r}="",N(Z${r})=0),"",(O${r}-Z${r})/Z${r})`,
    AD: `IF(D${r}="","",D${r}*CONFIG!$B$17)`,
    AE: `IF(OR(N(D${r})=0,G${r}=""),"",N(AD${r})-N(G${r}))`,
    AF: `IF(AE${r}="","",SUM(AE$${F9}:AE${r}))`,
    AG: `IF(AE${r}="","",IF(N(AD${r})<N(G${r}),"BAJO ALISTADO",IF(N(AD${r})>N(G${r})*(1+CONFIG!$B$18),"SOBRE TOLERANCIA","OK")))`,
  };
}

export function excelFormulasTotal() {
  const a = FIRST_ROW;
  const b = FIRST_ROW + ROWS - 1;
  const rng = (c) => `${c}${a}:${c}${b}`;
  const f = {};
  INPUT_COLS.forEach((c) => { f[c] = `SUM(${rng(c)})`; });
  Object.assign(f, {
    L: `IF(C${TOTAL_ROW}=0,0,D${TOTAL_ROW}/C${TOTAL_ROW})`,
    M: `IF(SUMIFS(${rng('F')},${rng('D')},">0")=0,0,(SUMIFS(${rng('D')},${rng('F')},">0")*CONFIG!$B$17-SUMIFS(${rng('F')},${rng('D')},">0"))/SUMIFS(${rng('F')},${rng('D')},">0"))`,
    N: `IF(I${TOTAL_ROW}=0,0,SUMIFS(${rng('C')},${rng('I')},">0")*1/I${TOTAL_ROW})`,
    O: `IF(I${TOTAL_ROW}=0,0,SUMIFS(${rng('D')},${rng('I')},">0")*CONFIG!$B$17/I${TOTAL_ROW})`,
    P: `IF(I${TOTAL_ROW}=0,0,SUMIFS(${rng('F')},${rng('I')},">0")*1/I${TOTAL_ROW})`,
    Q: `IF(I${TOTAL_ROW}=0,0,SUMIFS(${rng('G')},${rng('I')},">0")*1/I${TOTAL_ROW})`,
    R: `IF(I${TOTAL_ROW}=0,0,SUMIFS(${rng('H')},${rng('I')},">0")*1/I${TOTAL_ROW})`,
    X: `M${TOTAL_ROW}`,
    Y: `IF(G${TOTAL_ROW}=0,0,IFERROR((G${TOTAL_ROW}-SUMIFS(${rng('D')},${rng('G')},">0")*CONFIG!$B$17)/(SUMIFS(${rng('D')},${rng('G')},">0")*CONFIG!$B$17),0))`,
    Z: `O${TOTAL_ROW}`,
    AA: `Q${TOTAL_ROW}`,
    AB: `R${TOTAL_ROW}`,
    AD: `SUM(${rng('AD')})`,
    AE: `IF(COUNT(${rng('AE')})=0,"",SUM(${rng('AE')}))`,
    AG: `COUNTIF(${rng('AG')},"BAJO ALISTADO")&" bajo · "&COUNTIF(${rng('AG')},"SOBRE TOLERANCIA")&" sobre"`,
  });
  return f;
}

export function excelFormulasAverage() {
  const a = FIRST_ROW;
  const b = FIRST_ROW + ROWS - 1;
  const f = {};
  INPUT_COLS.forEach((c) => { f[c] = `IF(COUNT(${c}${a}:${c}${b})=0,0,AVERAGE(${c}${a}:${c}${b}))`; });
  return f;
}

// Fila 5 (banda de KPIs): celda -> fórmula
export const EXCEL_KPI_FORMULAS = {
  C5: `COUNT(D${FIRST_ROW}:D${FIRST_ROW + ROWS - 1})`,
  E5: `D${TOTAL_ROW}`,
  G5: `F${TOTAL_ROW}`,
  I5: `G${TOTAL_ROW}`,
  K5: `H${TOTAL_ROW}`,
  M5: `X${TOTAL_ROW}`,
  O5: `Y${TOTAL_ROW}`,
  Q5: `I${TOTAL_ROW}`,
  S5: `O${TOTAL_ROW}`,
  U5: `Q${TOTAL_ROW}`,
  W5: `R${TOTAL_ROW}`,
};

export const excelTitleFormula = (m) => `"Cliente: "&CONFIG!$B$4&"   |   Mes: ${MESES[m - 1]}   |   Año: "&CONFIG!$B$6`;
